import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { useTable, useSortBy, useRowSelect, Hooks, useFlexLayout, Row } from 'react-table';

import { SelectionOptions } from 'coreOld/enums';
import { useAppDispatch, useAppSelector, useReorderTableRows } from 'coreOld/hooks';
import { setSelectedRow, setSelectionMode } from 'coreOld/slices';

import { renderCheckboxes } from './Checkboxes';
import { renderInfoColumn } from './columns';
import TableView from './TableView';

interface IProps {
  data;
  columns;
  name: string;
  borderBottom?: boolean;
  borderTop?: boolean;
  borderRight?: boolean;
  headerLines?: number;
  footerLines?: number;
  alternating?: boolean;
  hasRowSelection?: boolean;
  scrollableTarget?: string;
  hasFooter?: boolean;
  checkboxFooter?: JSX.Element;
  hasBigSubheader?: boolean;
  hasFilters?: boolean;
  showInfoIcon?: boolean;
  isRowEditable?: boolean;
  onCheckboxClick?: () => void;
  isModalOpen?: boolean;
  renderEditButtons?: (hooks: Hooks<object>, isRowEditable: boolean) => void;
  isLoading?: boolean;
  onRowClick?;
  setSelectedRows?;
  fetchNextPage?;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  selectMenuOptions?: SelectionOptions[];
  defaultColumnStyle?;
  tableBodyStyle?: CSSProperties;
  fullWidth?: boolean;
  isScrollable?: boolean;
  bodyHeightAuto?: boolean;
  noItemsFoundText?: string;
  hasLineNumbers?: boolean;
  getRowLineNumber?: (row: Row) => number | string;
  isRowDeleted?: (item: any) => boolean;
  hasRowReordering?: boolean;
  onRowsReorder?: (diffRows, newRows) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getDefaultRowLineNumber = (row: Row<any>) => row?.original?.line;

const Table = ({
  name,
  columns,
  data,
  borderBottom = false,
  borderTop = false,
  borderRight = false,
  alternating = true,
  hasRowSelection = false,
  showInfoIcon = false,
  fullWidth = true,
  isScrollable = true,
  bodyHeightAuto = false,
  headerLines = 1,
  footerLines = 0,
  scrollableTarget = name,
  onRowClick,
  hasFilters,
  hasFooter,
  checkboxFooter,
  hasBigSubheader,
  setSelectedRows,
  onCheckboxClick,
  isModalOpen,
  renderEditButtons,
  isRowEditable,
  isLoading = false,
  selectMenuOptions,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  defaultColumnStyle,
  tableBodyStyle,
  noItemsFoundText,
  hasLineNumbers = false,
  getRowLineNumber = getDefaultRowLineNumber,
  isRowDeleted,
  hasRowReordering = false,
  onRowsReorder,
}: IProps) => {
  const [tableData, setTableData] = useState(data);
  const [initialized, setInitialized] = useState(false);

  const { selectionMode } = useAppSelector((state) => state.table);
  const dispatch = useAppDispatch();

  const defaultColumn = useMemo(
    () => ({
      minWidth: 30,
      width: 150,
      ...defaultColumnStyle,
    }),
    []
  );

  const onShowInfoClick = (row: Row) => dispatch(setSelectedRow(row.original));

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
    toggleAllRowsSelected,
  } = useTable(
    {
      columns,
      data: hasRowReordering ? tableData : data,
      defaultColumn,
      autoResetSelectedRows: false,
      disableSortBy: true,
    },
    useSortBy,
    useRowSelect,
    useFlexLayout,
    (hooks) => renderEditButtons && renderEditButtons(hooks, isRowEditable),
    (hooks) =>
      renderCheckboxes(
        hooks,
        hasRowSelection,
        selectMenuOptions,
        hasLineNumbers,
        getRowLineNumber,
        hasRowReordering,
        checkboxFooter,
        isRowDeleted
      ),
    (hooks) => renderInfoColumn(hooks, showInfoIcon, onShowInfoClick)
  );

  const {
    activeId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    selectedReorderingRow,
    sensors,
  } = useReorderTableRows(tableData, setTableData, rows, prepareRow, onRowsReorder);

  let reorderProps = {};
  if (hasRowReordering) {
    reorderProps = {
      hasRowReordering,
      activeId,
      selectedReorderingRow,
      sensors,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onDragCancel: handleDragCancel,
      modifiers: [restrictToVerticalAxis],
    };
  }

  useEffect(() => {
    if (data && !isLoading) {
      setTableData(data);
    }
  }, [data]);

  useEffect(() => {
    if (setSelectedRows && selectedFlatRows.length) {
      setSelectedRows(selectedFlatRows.map((item) => item.original));
    }

    if (selectedFlatRows?.length >= 1 && !initialized) {
      onCheckboxClick();
      setInitialized(true);
    }

    if (selectedFlatRows?.length === 0 && initialized) {
      onCheckboxClick();
      toggleAllRowsSelected(false);
      setInitialized(false);
    }
  }, [selectedFlatRows.length]);

  useEffect(() => {
    if (selectedFlatRows.length > 0 && !isModalOpen) {
      toggleAllRowsSelected(false);
      onCheckboxClick();
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (selectionMode === SelectionOptions.FILTERED) {
      toggleAllRowsSelected(true);
    }
  }, [isFetchingNextPage]);

  const handleOnRowClick = () => {
    dispatch(setSelectionMode(SelectionOptions.NONE));

    onRowClick && onRowClick();
  };

  return (
    <TableView
      rows={rows}
      prepareRow={prepareRow}
      onRowClick={handleOnRowClick}
      isLoading={isLoading}
      hasRowSelection={hasRowSelection}
      hasFooter={hasFooter}
      hasBigSubheader={hasBigSubheader}
      hasFilters={hasFilters}
      hasNextPage={hasNextPage}
      isScrollable={isScrollable}
      bodyHeightAuto={bodyHeightAuto}
      fetchNextPage={fetchNextPage}
      isFetchingNextPage={isFetchingNextPage}
      getTableProps={getTableProps}
      getTableBodyProps={getTableBodyProps}
      headerGroups={headerGroups}
      headerLines={headerLines}
      footerGroups={footerGroups}
      footerLines={footerLines}
      borderBottom={borderBottom}
      borderTop={borderTop}
      borderRight={borderRight}
      tableBodyStyle={tableBodyStyle}
      alternating={alternating}
      scrollableTarget={scrollableTarget}
      fullWidth={fullWidth}
      noItemsFoundText={noItemsFoundText}
      isRowDeleted={isRowDeleted}
      // reordering props
      hasRowReordering={hasRowReordering}
      {...reorderProps}
    />
  );
};

export default Table;
